export type LoginWelcomeUserId = string | number | null | undefined;

const STORAGE_KEY_PREFIX = 'suite241.login-welcome.pending.';
const pendingInMemory = new Set<string>();
const consumedInMemory = new Set<string>();

const markerKey = (userId: LoginWelcomeUserId): string | null => {
  if (userId === null || userId === undefined) return null;

  const normalizedUserId = String(userId).trim();
  if (!normalizedUserId) return null;

  return `${STORAGE_KEY_PREFIX}${encodeURIComponent(normalizedUserId)}`;
};

const getSessionStorage = (): Storage | null => {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
};

export function markLoginWelcomePending(userId: LoginWelcomeUserId): void {
  const key = markerKey(userId);
  if (!key) return;

  consumedInMemory.delete(key);
  pendingInMemory.add(key);

  try {
    getSessionStorage()?.setItem(key, '1');
  } catch {
    // Le Set mémoire conserve le marqueur si le stockage est indisponible.
  }
}

export function consumeLoginWelcomePending(userId: LoginWelcomeUserId): boolean {
  const key = markerKey(userId);
  if (!key || consumedInMemory.has(key)) return false;

  let isPending = pendingInMemory.has(key);
  const storage = getSessionStorage();

  try {
    isPending = isPending || storage?.getItem(key) === '1';
  } catch {
    // Le marqueur mémoire reste la source de secours.
  }

  if (!isPending) return false;

  pendingInMemory.delete(key);
  consumedInMemory.add(key);

  try {
    storage?.removeItem(key);
  } catch {
    // Le marqueur consommé en mémoire empêche une seconde consommation locale.
  }

  return true;
}

export function clearLoginWelcomePending(userId: LoginWelcomeUserId): void {
  const key = markerKey(userId);
  if (!key) return;

  pendingInMemory.delete(key);
  consumedInMemory.add(key);

  try {
    getSessionStorage()?.removeItem(key);
  } catch {
    // Le marqueur consommé en mémoire neutralise le stockage indisponible.
  }
}
